/**
 * Busca preditiva V6 (aprimoramento progressivo).
 *
 * O formulário GET nativo é a base: sem JavaScript, com o setting
 * desligado ou em erro de rede, a busca continua funcionando. Este
 * custom element consulta a Predictive Search API via Section Rendering
 * API e injeta a resposta num combobox/listbox acessível.
 *
 * - Uma instância por formulário (desktop, linha mobile, drawer);
 *   apenas uma aberta por vez; IDs de opção atribuídos por instância.
 * - Debounce ~250ms, AbortController por requisição (a última consulta
 *   prevalece), cache em memória por termo normalizado (sessão da página).
 * - Sem dependências, sem fetch de catálogo, sem listeners por resultado.
 */
(function () {
  'use strict';

  if (customElements.get('predictive-search')) return;

  var DEBOUNCE_MS = 250;
  var MIN_CHARS = 2;
  var cache = new Map();
  var instances = new Set();

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-predictive-input]');
      this.panel = this.querySelector('[data-predictive-panel]');
      this.listbox = this.querySelector('[data-predictive-content]');
      this.status = this.querySelector('[data-predictive-status]');
      this.form = this.querySelector('form');
      if (!this.input || !this.panel || !this.listbox) return;

      this.activeIndex = -1;
      this.currentTerm = '';
      this.abortController = null;
      this.debounceTimer = 0;

      this.onInput = this.handleInput.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onFocus = this.handleFocus.bind(this);
      this.onDocPointer = this.handleDocPointer.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onSubmit = this.close.bind(this);

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeydown);
      this.input.addEventListener('focus', this.onFocus);
      this.addEventListener('click', this.onClick);
      if (this.form) this.form.addEventListener('submit', this.onSubmit);
      document.addEventListener('pointerdown', this.onDocPointer);

      instances.add(this);
      this.toggleClear();
    }

    disconnectedCallback() {
      clearTimeout(this.debounceTimer);
      if (this.abortController) this.abortController.abort();
      document.removeEventListener('pointerdown', this.onDocPointer);
      instances.delete(this);
    }

    /* ------------------------------ eventos ----------------------------- */

    handleInput() {
      this.toggleClear();
      var term = this.input.value.trim();
      clearTimeout(this.debounceTimer);
      if (term.length < MIN_CHARS) {
        this.close();
        return;
      }
      var self = this;
      this.debounceTimer = setTimeout(function () {
        self.search(term);
      }, DEBOUNCE_MS);
    }

    handleFocus() {
      var term = this.input.value.trim();
      if (term.length < MIN_CHARS) return;
      var cached = cache.get(term.toLowerCase());
      if (cached !== undefined) this.render(cached);
    }

    handleClick(event) {
      var target = event.target;
      if (target.closest('[data-predictive-clear]')) {
        this.input.value = '';
        this.toggleClear();
        this.close();
        this.input.focus();
        return;
      }
      if (target.closest('[data-predictive-close]')) {
        this.close(); // não apaga a consulta
        this.input.focus();
      }
    }

    handleDocPointer(event) {
      if (!this.contains(event.target)) this.close();
    }

    handleKeydown(event) {
      var options = this.options();
      switch (event.key) {
        case 'ArrowDown':
          if (this.isOpen()) {
            event.preventDefault();
            this.setActive(Math.min(this.activeIndex + 1, options.length - 1));
          } else if (options.length) {
            event.preventDefault();
            this.open();
            this.setActive(0);
          }
          break;
        case 'ArrowUp':
          if (this.isOpen()) {
            event.preventDefault();
            this.setActive(Math.max(this.activeIndex - 1, 0));
          }
          break;
        case 'Home':
          if (this.isOpen() && options.length) {
            event.preventDefault();
            this.setActive(0);
          }
          break;
        case 'End':
          if (this.isOpen() && options.length) {
            event.preventDefault();
            this.setActive(options.length - 1);
          }
          break;
        case 'Enter':
          if (this.isOpen() && this.activeIndex >= 0 && options[this.activeIndex]) {
            event.preventDefault();
            options[this.activeIndex].click(); // navega para a opção ativa
          }
          // sem opção ativa: submit natural do form GET
          break;
        case 'Escape':
          if (this.isOpen()) {
            event.preventDefault();
            this.close(); // mantém o termo; foco continua no input
          }
          break;
        case 'Tab':
          this.close(); // sem foco preso; navegação normal
          break;
      }
    }

    /* ------------------------------- busca ------------------------------ */

    search(term) {
      var key = term.toLowerCase();
      var cached = cache.get(key);
      if (cached !== undefined) {
        this.currentTerm = term;
        this.render(cached);
        return;
      }

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.currentTerm = term;
      this.input.setAttribute('aria-busy', 'true');
      this.announce(this.dataset.textLoading || '');

      var url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('section_id', 'predictive-search');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[limit_scope]', 'all');
      url.searchParams.set('resources[type]', 'query,product,collection,page,article');

      var self = this;
      fetch(url.toString(), { signal: this.abortController.signal })
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then(function (html) {
          if (!self.isConnected || self.currentTerm !== term) return; // resposta antiga
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var results = doc.querySelector('#shopify-section-predictive-search [data-predictive-results]');
          var markup = results ? results.outerHTML : '';
          cache.set(key, markup);
          self.render(markup);
        })
        .catch(function (error) {
          if (error.name === 'AbortError') return;
          // Erro de rede/resposta: fecha em silêncio; o form GET permanece
          if (self.isConnected) self.close();
        })
        .finally(function () {
          if (self.isConnected) self.input.removeAttribute('aria-busy');
        });
    }

    render(markup) {
      this.listbox.innerHTML = markup;
      var results = this.listbox.querySelector('[data-predictive-results]');
      var total = results ? parseInt(results.dataset.total, 10) || 0 : 0;

      if (total === 0) {
        this.close();
        this.announce(this.dataset.textEmpty || '');
        return;
      }

      // IDs por instância (a mesma resposta serve a vários comboboxes)
      var prefix = this.input.id;
      this.options().forEach(function (option, index) {
        option.id = prefix + '-opt-' + index;
        option.setAttribute('aria-selected', 'false');
      });
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.open();
      this.announce((this.dataset.textResults || '').replace('[count]', String(total)));
    }

    /* ------------------------------ estado ------------------------------ */

    options() {
      return Array.prototype.slice.call(this.listbox.querySelectorAll('[data-predictive-option]'));
    }

    isOpen() {
      return !this.panel.hidden;
    }

    open() {
      instances.forEach(function (other) {
        if (other !== this) other.close();
      }, this);
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      if (!this.panel) return;
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
    }

    setActive(index) {
      var options = this.options();
      if (!options.length) return;
      if (this.activeIndex >= 0 && options[this.activeIndex]) {
        options[this.activeIndex].setAttribute('aria-selected', 'false');
        options[this.activeIndex].classList.remove('is-active');
      }
      this.activeIndex = index;
      var option = options[index];
      option.setAttribute('aria-selected', 'true');
      option.classList.add('is-active');
      this.input.setAttribute('aria-activedescendant', option.id);
      if (option.scrollIntoView) option.scrollIntoView({ block: 'nearest' });
    }

    toggleClear() {
      var clear = this.querySelector('[data-predictive-clear]');
      if (clear) clear.hidden = this.input.value.length === 0;
    }

    announce(text) {
      if (this.status) this.status.textContent = text;
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
